import { useTrackingCode } from '@hotjar/webapp/hooks/useTrackingCode';
import { useCurrentSite } from '@hotjar/site';
import type { ResponseLimitLocation } from '../ResponseLimitAlert/ResponseLimitAlert';
import {
  ResponseLimitAlert,
  useShowResponseLimitAlert,
} from '../ResponseLimitAlert/ResponseLimitAlert';
import { useShowWidgetLimitAlert, WidgetLimitAlert } from '../WidgetLimitAlert/WidgetLimitAlert';
import { AskPricingChangeAlert } from '../AskPricingChangeAlert/AskPricingChangeAlert';

export type LimitAlertsProps = {
  featureGroup: 'surveys' | 'feedback';
  location: ResponseLimitLocation;
  skippedInstallation: boolean | undefined;
  hidePricingAlert?: boolean;
};

export const LimitAlerts = ({
  featureGroup,
  location,
  skippedInstallation,
  hidePricingAlert = false,
}: LimitAlertsProps): JSX.Element | null => {
  const showResponseLimitAlert = useShowResponseLimitAlert({ location, featureGroup });
  const { getState } = useTrackingCode();
  const { data: site } = useCurrentSite();
  const trackingCodeState = getState(site?.lastData);

  const zeroResponseAlertVisible =
    showResponseLimitAlert.visibility === 'visible' && showResponseLimitAlert.props.remaining === 0;

  const showWidgetLimitAlert = useShowWidgetLimitAlert({
    featureGroup,
    isEnabled:
      showResponseLimitAlert.visibility === 'loading' || skippedInstallation === undefined
        ? undefined
        : (showResponseLimitAlert.visibility === 'hidden' || !zeroResponseAlertVisible) &&
          (trackingCodeState.installed || skippedInstallation),
  });

  if (
    showResponseLimitAlert.visibility === 'loading' ||
    showWidgetLimitAlert.visibility === 'loading'
  ) {
    return null;
  }

  if (showResponseLimitAlert.visibility === 'visible' && zeroResponseAlertVisible) {
    return <ResponseLimitAlert {...showResponseLimitAlert.props} />;
  }

  if (showWidgetLimitAlert.visibility === 'visible') {
    return <WidgetLimitAlert {...showWidgetLimitAlert.props} />;
  }

  if (showResponseLimitAlert.visibility === 'visible') {
    return <ResponseLimitAlert {...showResponseLimitAlert.props} />;
  }

  return hidePricingAlert ? null : <AskPricingChangeAlert location={location} />;
};
